import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog"
import { useEffect, useState } from "react"
import { useUrlImageQuery } from "../../query/urlImageQuery"

// Icônes SVG pour la navigation
const ChevronLeftIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    {...props}
  >
    <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
  </svg>
)

const ChevronRightIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    {...props}
  >
    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
  </svg>
)

const ExpandIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    {...props}
  >
    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
  </svg>
)

const TrashIcon = (props: React.SVGProps<SVGSVGElement>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    {...props}
  >
    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" />
  </svg>
)

type ShopImageProps = {
  initialImagePath: string
  altText: string
  allImagePaths: string[]
  initialImageIndex: number
  onDelete?: (imageIndex: number, imagePath: string) => void
}

export const ShopImage = ({
  initialImagePath,
  altText,
  allImagePaths,
  initialImageIndex,
  onDelete,
}: ShopImageProps) => {
  const [currentModalImageIndex, setCurrentModalImageIndex] =
    useState(initialImageIndex)
  const modalImagePathToLoad = allImagePaths[currentModalImageIndex]

  const {
    urlImage: thumbnailUrl,
    pendingUrlImage: pendingThumbnail,
    errorUrlImage: errorThumbnail,
  } = useUrlImageQuery(initialImagePath)
  const {
    urlImage: modalImageUrl,
    pendingUrlImage: pendingModalImage,
    errorUrlImage: errorModalImage,
    refetchUrlImage,
  } = useUrlImageQuery(modalImagePathToLoad)

  const [isModalOpen, setIsModalOpen] = useState(false)

  useEffect(() => {
    if (isModalOpen && modalImagePathToLoad) {
      refetchUrlImage()
    }
  }, [modalImagePathToLoad, isModalOpen, refetchUrlImage])

  const goToPrevious = (e: React.MouseEvent) => {
    e.stopPropagation()
    setCurrentModalImageIndex((prevIndex) =>
      prevIndex > 0 ? prevIndex - 1 : allImagePaths.length - 1
    )
  }

  const goToNext = (e: React.MouseEvent) => {
    e.stopPropagation()
    setCurrentModalImageIndex((prevIndex) =>
      prevIndex < allImagePaths.length - 1 ? prevIndex + 1 : 0
    )
  }

  if (pendingThumbnail) {
    return (
      <div className="group relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 shadow-md">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex flex-col items-center space-y-2">
            <div className="w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-xs font-medium text-slate-600 dark:text-slate-400">
              Chargement...
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (errorThumbnail || !thumbnailUrl) {
    return (
      <div className="group relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-gradient-to-br from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20 shadow-md border border-red-200 dark:border-red-800">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex flex-col items-center space-y-2">
            <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <span className="text-red-500 text-lg">⚠</span>
            </div>
            <p className="text-xs font-medium text-red-600 dark:text-red-400 text-center px-2">
              Erreur de chargement
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <Dialog
      open={isModalOpen}
      onOpenChange={(open: boolean) => {
        setIsModalOpen(open)
        if (open) {
          setCurrentModalImageIndex(initialImageIndex)
        }
      }}
    >
      <DialogTrigger asChild>
        <div className="group relative aspect-[4/3] w-full overflow-hidden rounded-xl bg-slate-100 dark:bg-slate-800 shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer">
          <img
            src={thumbnailUrl}
            alt={altText}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />

          {/* Overlay avec effet de survol */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-white/90 dark:bg-slate-800/90 rounded-full p-3 shadow-lg backdrop-blur-sm">
              <ExpandIcon className="w-5 h-5 text-slate-700 dark:text-slate-200" />
            </div>
          </div>

          {/* Indicateur de position dans la galerie */}
          <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full font-medium backdrop-blur-sm">
            {initialImageIndex + 1}/{allImagePaths.length}
          </div>

          {/* Icône de suppression au survol */}
          {onDelete && (
            <button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                onDelete(initialImageIndex, initialImagePath)
              }}
              className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 shadow-lg backdrop-blur-sm z-10"
              aria-label="Supprimer l'image"
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </DialogTrigger>

      <DialogContent className="max-w-7xl min-w-[80vw] min-h-[80vh] w-[80vw] sm:w-[90vw] h-auto max-h-[95vh] p-0 flex flex-col items-center justify-center bg-white/95 border-none shadow-2xl overflow-hidden">
        <div className="relative w-full h-full flex items-center justify-center p-4">
          {pendingModalImage && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm">
              <div className="flex flex-col items-center space-y-4">
                <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
                <p className="text-white text-lg font-medium">
                  Chargement de l'image...
                </p>
              </div>
            </div>
          )}

          {errorModalImage && !pendingModalImage && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/80 p-6">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mb-4">
                  <span className="text-red-400 text-2xl">⚠</span>
                </div>
                <p className="text-red-400 text-xl font-semibold">
                  Erreur de chargement
                </p>
                <p className="text-slate-300 text-sm max-w-md">
                  Impossible d'afficher cette image pour le moment. Veuillez
                  réessayer plus tard.
                </p>
              </div>
            </div>
          )}

          {!pendingModalImage && !errorModalImage && modalImageUrl && (
            <div className="relative max-w-full max-h-full">
              <img
                src={modalImageUrl}
                alt={altText}
                className="max-w-full max-h-[88vh] object-contain rounded-lg shadow-2xl"
              />

              {/* Indicateur de position en modal */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm font-medium backdrop-blur-sm">
                {currentModalImageIndex + 1} / {allImagePaths.length}
              </div>
            </div>
          )}

          {allImagePaths.length > 1 && (
            <>
              <button
                onClick={goToPrevious}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white bg-black/40 hover:bg-black/70 rounded-full p-3 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-200 z-20 backdrop-blur-sm"
                aria-label="Image précédente"
              >
                <ChevronLeftIcon className="w-6 h-6" />
              </button>
              <button
                onClick={goToNext}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white bg-black/40 hover:bg-black/70 rounded-full p-3 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all duration-200 z-20 backdrop-blur-sm"
                aria-label="Image suivante"
              >
                <ChevronRightIcon className="w-6 h-6" />
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
